import React from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sun, Moon, Palette, Check } from 'lucide-react';

const ThemeSelector = ({ variant = 'default' }) => {
  const { currentTheme, setTheme, themes, getThemesByCategory, isDark } = useTheme();

  const categories = [
    { id: 'base', label: 'Basic Themes', icon: '🎨' },
    { id: 'starwars', label: 'Star Wars', icon: '⭐' },
    { id: 'bigbang', label: 'Big Bang Theory', icon: '🔬' },
    { id: 'marvel', label: 'Marvel Comics', icon: '🦸' },
    { id: 'dc', label: 'DC Comics', icon: '🦇' },
  ];

  if (variant === 'simple') {
    return (
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setTheme(isDark ? 'light' : 'dark')}
        className="text-white/70 hover:text-white hover:bg-white/10"
      >
        {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </Button>
    );
  }

  const triggerClass = variant === 'sidebar' 
    ? "h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
    : "gap-2";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size={variant === 'sidebar' ? 'icon' : 'sm'} className={triggerClass}>
          <Palette className="w-4 h-4" />
          {variant !== 'sidebar' && (
            <>
              <span className="hidden md:inline">Theme</span>
              {themes[currentTheme]?.icon && (
                <span>{themes[currentTheme].icon}</span>
              )}
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 max-h-96 overflow-y-auto">
        {categories.map((category) => {
          const categoryThemes = getThemesByCategory(category.id);
          if (categoryThemes.length === 0) return null;
          
          return (
            <React.Fragment key={category.id}>
              <DropdownMenuLabel className="flex items-center gap-2">
                <span>{category.icon}</span>
                {category.label}
              </DropdownMenuLabel>
              {categoryThemes.map((theme) => (
                <DropdownMenuItem
                  key={theme.id}
                  onClick={() => setTheme(theme.id)}
                  className="flex items-center justify-between cursor-pointer"
                >
                  <div className="flex items-center gap-2">
                    {theme.icon && <span>{theme.icon}</span>}
                    <span>{theme.name}</span>
                  </div>
                  {currentTheme === theme.id && (
                    <Check className="w-4 h-4 text-primary" />
                  )}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
            </React.Fragment>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ThemeSelector;
